import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import { AuthService } from '../../services/auth.service';

/**
 * Runtime accept-invite page (tenant-invite-flow). Reached via the tokenized email link at
 * `…/accept-invite?token=…`. Validates the token (checkInvite), lets the invited person set a
 * password to accept, then auto-signs them in. The page knows its projectId from window.ZAVO_RUNTIME
 * (via AuthService.getRuntimeProject). Public — no auth guard, same as resetPassword.
 */
@Component({
  selector: 'app-accept-invite',
  imports: [FormsModule, CommonModule],
  templateUrl: './accept-invite.component.html',
})
export class AcceptInviteComponent {
  static readonly INVITE_EXPIRED = 'This invitation has expired. Ask whoever invited you to send it again.';
  readonly expiredText = AcceptInviteComponent.INVITE_EXPIRED;
  token = '';
  loading = true;
  valid = false;
  /** The link is real but past its 30 days (checkInvite `expired`, or acceptInvite 410): the page says so. */
  expired = false;
  email = '';
  appName = 'this app';
  roleName = '';
  userName = '';
  password = '';
  confirmPassword = '';
  submitting = false;
  errorMsg = '';
  done = false;
  /** 'signup' = set-password + create account; 'attach_email' = one-click confirm an email on an existing account. */
  kind = 'signup';

  constructor(private route: ActivatedRoute, private router: Router, private authService: AuthService) {
    this.route.queryParams.pipe(take(1)).subscribe(async (params) => {
      this.token = (params['token'] || '').toString();
      if (!this.token) { this.loading = false; this.valid = false; return; }
      const r = await this.authService.checkInvite(this.token);
      this.valid = r.valid;
      this.expired = !r.valid && r.expired === true;
      this.email = r.email || '';
      this.appName = r.appName || 'this app';
      this.roleName = r.roleName || '';
      this.kind = r.kind || 'signup';
      this.loading = false;
    });
  }

  /** attach_email invites: one click confirms the email onto the existing account (no password). */
  async confirmEmail(): Promise<void> {
    this.errorMsg = '';
    this.submitting = true;
    const status = await this.authService.confirmEmailInvite(this.token);
    this.submitting = false;
    if (status === 'success') { this.done = true; }
    else {
      if (/no longer valid|already in use|already linked/i.test(status)) { this.valid = false; }
      this.errorMsg = status;
    }
  }

  async accept(): Promise<void> {
    this.errorMsg = '';
    if ((this.password || '').length < 6) { this.errorMsg = 'Password must be at least 6 characters.'; return; }
    if (this.confirmPassword !== this.password) { this.errorMsg = 'Passwords do not match.'; return; }
    this.submitting = true;
    const status = await this.authService.acceptInvitePublic(
      this.email, this.password, this.token, (this.userName || '').trim() || undefined
    );
    this.submitting = false;
    if (status === 'success') {
      this.done = true;
      setTimeout(() => this.router.navigate(['/']), 900);
    } else {
      // A no-longer-valid token invalidates the whole page (someone else accepted, or it was cancelled).
      if (/no longer valid|already exists/i.test(status)) { this.valid = false; }
      // An expired link (410) is the same dead end, said plainly.
      if (status === AcceptInviteComponent.INVITE_EXPIRED) { this.valid = false; this.expired = true; }
      this.errorMsg = status;
    }
  }

  navigateHome(): void { this.router.navigate(['/']); }
}
